import { supabase } from '../config/supabase.js'
import { navigateToPerson } from '../router/router.js'
import { showSuccess, showError } from '../utils/toast.js'
import { showConfirm } from '../utils/confirm-modal.js'
import './person-edit.css'

let currentPerson = null
let currentEvents = []

export async function loadPersonEdit(personId) {
  const contentDiv = document.getElementById('person-edit-content')
  contentDiv.innerHTML = '<div class="loading">Loading person data...</div>'
  
  try {
    // Fetch person with names and events
    const { data: person, error: personError } = await supabase
      .from('person')
      .select(`
        *,
        person_name (*),
        event (*)
      `)
      .eq('id', personId)
      .single()
    
    if (personError) throw personError
    
    currentPerson = person
    currentEvents = [...person.event]
    
    renderEditForm(person)
    
  } catch (error) {
    console.error('Error loading person for edit:', error)
    contentDiv.innerHTML = `
      <div class="error">
        <strong>Error loading person:</strong> ${error.message}
      </div>
    `
  }
}

function renderEditForm(person) {
  const primaryName = person.person_name.find(n => n.is_primary) || person.person_name[0]
  const birthEvent = person.event.find(e => e.event_type === 'birth')
  const deathEvent = person.event.find(e => e.event_type === 'death')
  
  // Parse birth date
  let birthMonth = ''
  let birthYear = ''
  if (birthEvent?.date_text) {
    const birthDate = parseDateText(birthEvent.date_text)
    birthMonth = birthDate.month
    birthYear = birthDate.year
  }
  
  // Parse death date
  let deathMonth = ''
  let deathYear = ''
  if (deathEvent?.date_text) {
    const deathDate = parseDateText(deathEvent.date_text)
    deathMonth = deathDate.month
    deathYear = deathDate.year
  }
  
  // Other events (not birth/death)
  const otherEvents = currentEvents.filter(e => e.event_type !== 'birth' && e.event_type !== 'death')
  
  const html = `
    <div class="edit-header">
      <h2>Edit Person</h2>
      <div class="edit-actions">
        <button type="button" class="btn-secondary" id="cancel-edit-btn">Cancel</button>
        <button type="button" class="btn-primary" id="save-person-btn">Save Changes</button>
      </div>
    </div>
    
    <form id="person-edit-form" class="edit-form">
      <!-- Basic Information -->
      <div class="form-section">
        <h3>Basic Information</h3>
        
        <div class="form-row">
          <div class="form-group">
            <label for="first-name">First Name *</label>
            <input type="text" id="first-name" required 
                   value="${escapeHtml(getFirstName(primaryName.full_name))}">
          </div>

          <div class="form-group">
            <label for="last-name">Last Name</label>
            <input type="text" id="last-name" 
                    value="${escapeHtml(getLastName(primaryName.full_name))}">
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label for="sex">Sex</label>
            <select id="sex">
              <option value="">Unknown</option>
              <option value="M" ${person.sex === 'M' ? 'selected' : ''}>Male</option>
              <option value="F" ${person.sex === 'F' ? 'selected' : ''}>Female</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="privacy-level">Privacy Level</label>
            <select id="privacy-level">
              <option value="public" ${person.privacy_level === 'public' ? 'selected' : ''}>Public</option>
              <option value="family" ${person.privacy_level === 'family' ? 'selected' : ''}>Family</option>
              <option value="private" ${person.privacy_level === 'private' ? 'selected' : ''}>Private</option>
            </select>
          </div>
        </div>
      </div>
      
      <!-- Birth Information -->
      <div class="form-section">
        <h3>Birth Information</h3>
        
        <div class="form-row">
          <div class="form-group">
            <label for="birth-month">Birth Month</label>
            <select id="birth-month">
              <option value="">Unknown</option>
              ${generateMonthOptions(birthMonth)}
            </select>
          </div>
          
          <div class="form-group">
            <label for="birth-year">Birth Year</label>
            <input type="number" id="birth-year" min="1700" max="${new Date().getFullYear()}" 
                   placeholder="YYYY" value="${birthYear}">
          </div>
        </div>
        
        <div class="form-group">
          <label for="birth-place">Birth Place</label>
          <input type="text" id="birth-place" placeholder="City, State, Country" 
                 value="${escapeHtml(birthEvent?.place_text || '')}">
        </div>
      </div>
      
      <!-- Death Information -->
      <div class="form-section">
        <h3>Death Information</h3>
        
        ${!deathEvent ? `
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" id="is-living" ${person.is_living ? 'checked' : ''}>
              <span>Person is living</span>
            </label>
          </div>
          
          <div id="death-fields" class="death-fields ${person.is_living ? 'hidden' : ''}">
        ` : '<div id="death-fields" class="death-fields">'}
        
          <div class="form-row">
            <div class="form-group">
              <label for="death-month">Death Month</label>
              <select id="death-month">
                <option value="">Unknown</option>
                ${generateMonthOptions(deathMonth)}
              </select>
            </div>
            
            <div class="form-group">
              <label for="death-year">Death Year</label>
              <input type="number" id="death-year" min="1700" max="${new Date().getFullYear()}" 
                     placeholder="YYYY" value="${deathYear}">
            </div>
          </div>
          
          <div class="form-group">
            <label for="death-place">Death Place</label>
            <input type="text" id="death-place" placeholder="City, State, Country" 
                   value="${escapeHtml(deathEvent?.place_text || '')}">
          </div>
        </div>
      </div>
      
      <!-- Other Life Events -->
      <div class="form-section">
        <h3>Other Life Events</h3>
        <div id="events-list">
          ${otherEvents.map((event, idx) => renderEventForm(event, idx)).join('')}
        </div>
        <button type="button" class="btn-secondary" id="add-event-btn">+ Add Event</button>
      </div>
      
      <!-- Notes -->
      <div class="form-section">
        <h3>Notes</h3>
        <div class="form-group">
          <textarea id="notes" rows="6" placeholder="Additional notes about this person...">${escapeHtml(person.notes || '')}</textarea>
        </div>
      </div>
    </form>
  `
  
  document.getElementById('person-edit-content').innerHTML = html
  
  // Set up event listeners
  setupEventListeners(person.id)
}

function renderEventForm(event, index) {
  const date = parseDateText(event.date_text)
  
  return `
    <div class="event-form" data-event-index="${index}">
      <div class="event-form-header">
        <h4>Event #${index + 1}</h4>
        <button type="button" class="btn-delete" data-event-index="${index}">Remove</button>
      </div>
      
      <div class="form-row">
        <div class="form-group">
          <label>Event Type</label>
          <select class="event-type" data-event-index="${index}">
            <option value="residence" ${event.event_type === 'residence' ? 'selected' : ''}>Residence</option>
            <option value="marriage" ${event.event_type === 'marriage' ? 'selected' : ''}>Marriage</option>
            <option value="divorce" ${event.event_type === 'divorce' ? 'selected' : ''}>Divorce</option>
            <option value="graduation" ${event.event_type === 'graduation' ? 'selected' : ''}>Graduation</option>
            <option value="occupation" ${event.event_type === 'occupation' ? 'selected' : ''}>Occupation</option>
            <option value="immigration" ${event.event_type === 'immigration' ? 'selected' : ''}>Immigration</option>
            <option value="other" ${event.event_type === 'other' ? 'selected' : ''}>Other</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Month</label>
          <select class="event-month" data-event-index="${index}">
            <option value="">Unknown</option>
            ${generateMonthOptions(date.month)}
          </select>
        </div>
        
        <div class="form-group">
          <label>Year</label>
          <input type="number" class="event-year" data-event-index="${index}" 
                 min="1700" max="${new Date().getFullYear()}" placeholder="YYYY" value="${date.year}">
        </div>
      </div>
      
      <div class="form-group">
        <label>Place</label>
        <input type="text" class="event-place" data-event-index="${index}" 
               placeholder="City, State, Country" value="${escapeHtml(event.place_text || '')}">
      </div>
      
      <div class="form-group">
        <label>Description</label>
        <textarea class="event-description" data-event-index="${index}" 
                  rows="2" placeholder="Additional details...">${escapeHtml(event.description || '')}</textarea>
      </div>
    </div>
  `
}

function setupEventListeners(personId) {
  // Cancel button
  document.getElementById('cancel-edit-btn').addEventListener('click', async () => {
    const confirmed = await showConfirm('Discard changes and return to person details?', {
      title: 'Discard Changes',
      confirmText: 'Discard',
      cancelText: 'Keep Editing'
    })
    
    if (confirmed) {
      navigateToPerson(personId)
    }
  })
  
  // Save button
  document.getElementById('save-person-btn').addEventListener('click', () => {
    savePerson(personId)
  })
  
  // Living status toggle
  const isLivingCheckbox = document.getElementById('is-living')
  if (isLivingCheckbox) {
    isLivingCheckbox.addEventListener('change', (e) => {
      const deathFields = document.getElementById('death-fields')
      if (e.target.checked) {
        deathFields.classList.add('hidden')
      } else {
        deathFields.classList.remove('hidden')
      }
    })
  }
  
  // Add event button
  document.getElementById('add-event-btn').addEventListener('click', () => {
    const newEvent = {
      event_type: 'other',
      date_text: '',
      place_text: '',
      description: ''
    }
    currentEvents.push(newEvent)
    
    const eventsList = document.getElementById('events-list')
    const otherEvents = currentEvents.filter(e => e.event_type !== 'birth' && e.event_type !== 'death')
    eventsList.innerHTML = otherEvents.map((event, idx) => renderEventForm(event, idx)).join('')
    
    // Re-attach delete listeners
    attachEventDeleteListeners()
  })
  
  // Event delete buttons
  attachEventDeleteListeners()
}

function attachEventDeleteListeners() {
  document.querySelectorAll('.btn-delete').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const index = parseInt(e.target.dataset.eventIndex)
      
      const confirmed = await showConfirm('Remove this event?', {
        title: 'Remove Event',
        confirmText: 'Remove',
        cancelText: 'Cancel',
        confirmClass: 'confirm-btn-danger'
      })
      
      if (confirmed) {
        // Find the actual event in currentEvents (skipping birth/death)
        const otherEvents = currentEvents.filter(ev => ev.event_type !== 'birth' && ev.event_type !== 'death')
        const eventToRemove = otherEvents[index]
        const actualIndex = currentEvents.indexOf(eventToRemove)
        currentEvents.splice(actualIndex, 1)
        
        // Re-render events list
        const eventsList = document.getElementById('events-list')
        const remainingEvents = currentEvents.filter(e => e.event_type !== 'birth' && e.event_type !== 'death')
        eventsList.innerHTML = remainingEvents.map((event, idx) => renderEventForm(event, idx)).join('')
        
        // Re-attach listeners
        attachEventDeleteListeners()
      }
    })
  })
}

async function savePerson(personId) {
  const saveBtn = document.getElementById('save-person-btn')
  saveBtn.disabled = true
  saveBtn.textContent = 'Saving...'
  
  try {
    // Collect form data
    const firstName = document.getElementById('first-name').value.trim()
    const lastName = document.getElementById('last-name').value.trim()
    const sex = document.getElementById('sex').value
    const privacyLevel = document.getElementById('privacy-level').value
    const notes = document.getElementById('notes').value.trim()
    
      
      // Highlight empty fields
      if (!firstName) {
        showError('First name is required')
        
        // Highlight empty field
        document.getElementById('first-name').classList.add('error')
        
        saveBtn.disabled = false
        saveBtn.textContent = 'Save Changes'
        return
      }

      // Remove error highlight if present
      document.getElementById('first-name').classList.remove('error')

      const fullName = lastName ? `${firstName} ${lastName}` : firstName
    
    // Determine living status
    const isLivingCheckbox = document.getElementById('is-living')
    const deathMonth = document.getElementById('death-month').value
    const deathYear = document.getElementById('death-year').value
    const hasDeathDate = deathMonth && deathYear
    const isLiving = hasDeathDate ? false : (isLivingCheckbox ? isLivingCheckbox.checked : false)
    
    // Update person record
    const { error: personError } = await supabase
      .from('person')
      .update({
        sex: sex || null,
        privacy_level: privacyLevel,
        is_living: isLiving,
        notes: notes || null,
      })
      .eq('id', personId)
    
    if (personError) throw personError
    
    // Update primary name
    const primaryName = currentPerson.person_name.find(n => n.is_primary)
    if (primaryName) {
      const { error: nameError } = await supabase
        .from('person_name')
        .update({
          full_name: fullName
        })
        .eq('id', primaryName.id)
      
      if (nameError) throw nameError
    }
    
    // Handle birth event
    const birthMonth = document.getElementById('birth-month').value
    const birthYear = document.getElementById('birth-year').value
    const birthPlace = document.getElementById('birth-place').value.trim()
    
    const birthEvent = currentPerson.event.find(e => e.event_type === 'birth')
    if (birthMonth && birthYear) {
      const birthDateText = birthMonth ? `01 ${birthMonth} ${birthYear}` : birthYear
      const birthDateSort = birthMonth ? `${birthYear}-${getMonthNumber(birthMonth)}-01` : `${birthYear}-10-16`
      
      if (birthEvent) {
        const { error: birthError } = await supabase
          .from('event')
          .update({
            date_text: birthDateText,
            date_sort: birthDateSort,
            place_text: birthPlace || null
          })
          .eq('id', birthEvent.id)
        
        if (birthError) throw birthError
      } else {
        // Create new
        const { error: birthError } = await supabase
          .from('event')
          .insert({
            person_id: personId,
            event_type: 'birth',
            date_text: birthDateText,
            date_sort: birthDateSort,
            place_text: birthPlace || null
          })
        
        if (birthError) throw birthError
      }
    } else if (birthEvent && !birthYear) {
      // Remove birth event if year is cleared
      const { error: deleteError } = await supabase
        .from('event')
        .delete()
        .eq('id', birthEvent.id)
      
      if (deleteError) throw deleteError
    }
    
    // Handle death event
    const deathPlace = document.getElementById('death-place').value.trim()
    const deathEvent = currentPerson.event.find(e => e.event_type === 'death')
    
    if (hasDeathDate) {
      const deathDateText = deathMonth ? `01 ${deathMonth} ${deathYear}` : deathYear
      const deathDateSort = deathMonth ? `${deathYear}-${getMonthNumber(deathMonth)}-01` : `${deathYear}-10-16`
      
      if (deathEvent) {
        // Update existing
        const { error: deathError } = await supabase
          .from('event')
          .update({
            date_text: deathDateText,
            date_sort: deathDateSort,
            place_text: deathPlace || null
          })
          .eq('id', deathEvent.id)
        
        if (deathError) throw deathError
      } else {
        // Create new
        const { error: deathError } = await supabase
          .from('event')
          .insert({
            person_id: personId,
            event_type: 'death',
            date_text: deathDateText,
            date_sort: deathDateSort,
            place_text: deathPlace || null
          })
        
        if (deathError) throw deathError
      }
    } else if (deathEvent && !deathYear) {
      // Remove death event if cleared
      const { error: deleteError } = await supabase
        .from('event')
        .delete()
        .eq('id', deathEvent.id)
      
      if (deleteError) throw deleteError
    }
    
    // Handle other events
    const otherEvents = currentEvents.filter(e => e.event_type !== 'birth' && e.event_type !== 'death')
    
    // Delete all existing other events
    const existingOtherEvents = currentPerson.event.filter(e => e.event_type !== 'birth' && e.event_type !== 'death')
    for (const event of existingOtherEvents) {
      const { error: deleteError } = await supabase
        .from('event')
        .delete()
        .eq('id', event.id)
      
      if (deleteError) throw deleteError
    }
    
    // Insert updated events
    for (let i = 0; i < otherEvents.length; i++) {
      const eventType = document.querySelector(`.event-type[data-event-index="${i}"]`).value
      const eventMonth = document.querySelector(`.event-month[data-event-index="${i}"]`).value
      const eventYear = document.querySelector(`.event-year[data-event-index="${i}"]`).value
      const eventPlace = document.querySelector(`.event-place[data-event-index="${i}"]`).value.trim()
      const eventDescription = document.querySelector(`.event-description[data-event-index="${i}"]`).value.trim()
      
      if (eventYear) {
        const dateText = eventMonth ? `01 ${eventMonth} ${eventYear}` : eventYear
        const dateSort = eventMonth ? `${eventYear}-${getMonthNumber(eventMonth)}-01` : `${eventYear}-01-01`
        
        const { error: eventError } = await supabase
          .from('event')
          .insert({
            person_id: personId,
            event_type: eventType,
            date_text: dateText,
            date_sort: dateSort,
            place_text: eventPlace || null,
            description: eventDescription || null
          })
        
        if (eventError) throw eventError
      }
    }
    
    // Success! Navigate back to detail page
    showSuccess('Person updated successfully!')
    navigateToPerson(personId)
    
  } catch (error) {
    console.error('Error saving person:', error)
    showError(`Error saving person: ${error.message}`)
  } finally {
    saveBtn.disabled = false
    saveBtn.textContent = 'Save Changes'
  }
}

// Helper functions
function parseDateText(dateText) {
  if (!dateText) return { month: '', year: '' }
  
  // Handle "DD MMM YYYY" format (like "30 JUL 1980")
  const fullDateMatch = dateText.match(/^\d+\s+([A-Z]{3})\s+(\d{4})$/)
  if (fullDateMatch) {
    return {
      month: fullDateMatch[1], // Uppercase 3-letter month
      year: fullDateMatch[2]
    }
  }
  
  // Handle "YYYY" only format (like "1950")
  const yearOnlyMatch = dateText.match(/^(\d{4})$/)
  if (yearOnlyMatch) {
    return {
      month: '',
      year: yearOnlyMatch[1]
    }
  }
  
  return { month: '', year: '' }
}

function getFirstName(fullName) {
  const parts = fullName.split(' ')
  return parts[0] || ''
}

function getLastName(fullName) {
  const parts = fullName.split(' ')
  return parts.slice(1).join(' ') || ''
}

function generateMonthOptions(selected) {
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  return months.map(month => 
    `<option value="${month}" ${month === selected ? 'selected' : ''}>${month}</option>`
  ).join('')
}

function getMonthNumber(monthAbbr) {
  const months = {
    'JAN': '01', 'FEB': '02', 'MAR': '03', 'APR': '04',
    'MAY': '05', 'JUN': '06', 'JUL': '07', 'AUG': '08',
    'SEP': '09', 'OCT': '10', 'NOV': '11', 'DEC': '12'
  }
  return months[monthAbbr] || '01'
}

function escapeHtml(text) {
  if (!text) return ''
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

export function showPersonEdit() {
  document.getElementById('content').classList.add('hidden')
  document.getElementById('person-detail-page').classList.add('hidden')
  document.getElementById('person-add-page').classList.add('hidden')
  document.getElementById('password-setup-page').classList.add('hidden')
  document.getElementById('user-profile-page').classList.add('hidden')  // ADD THIS
  document.getElementById('person-edit-page').classList.remove('hidden')
}
